import React, { useState, useEffect, useRef } from 'react'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TablePagination from '@mui/material/TablePagination'
import Paper from '@mui/material/Paper'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Stack from '@mui/material/Stack'
import Chip from '@mui/material/Chip'

import { VARIABLES_CONFIG, MIN_YEAR } from './datosAbiertosConfig'
import EstacionesModal from './EstacionesModal'
import globalConfig from '../../config/globalConfig'

// ─── helpers ──────────────────────────────────────────────────────────────────

const todayStr = () => new Date().toISOString().slice(0, 10)

const daysAgoStr = (n) => {
    const d = new Date()
    d.setDate(d.getDate() - n)
    return d.toISOString().slice(0, 10)
}

const getYearsInRange = (from, to) => {
    const y1 = parseInt(from.slice(0, 4))
    const y2 = parseInt(to.slice(0, 4))
    const years = []
    for (let y = y1; y <= y2; y++) years.push(y)
    return years
}

/**
 * Formats an ISO date string as DD/MM/YYYY HH:mm
 * without timezone conversion (preserves the original offset).
 */
const formatFecha = (isoStr) => {
    if (!isoStr) return ''
    const [datePart, rest = ''] = isoStr.split('T')
    const [year, month, day] = datePart.split('-')
    const time = rest.slice(0, 5)
    return `${day}/${month}/${year}${time ? ' ' + time : ''}`
}

// ─── component ────────────────────────────────────────────────────────────────

const DatosAbiertos = () => {
    const [selectedFolder, setSelectedFolder] = useState(VARIABLES_CONFIG[0].folder)
    const [selectedEstId,  setSelectedEstId]  = useState(null)
    const [dateFrom,       setDateFrom]        = useState(daysAgoStr(30))
    const [dateTo,         setDateTo]          = useState(todayStr())
    const [estaciones,     setEstaciones]      = useState([])
    const [rows,           setRows]            = useState([])
    const [loading,        setLoading]         = useState(false)
    const [error,          setError]           = useState(null)
    const [page,           setPage]            = useState(0)
    const [rowsPerPage,    setRowsPerPage]     = useState(25)
    const [modalEstaciones, setModalEstaciones] = useState(false)

    // File cache: key = `${folder}_${year}` → parsed JSON object
    const cache = useRef({})

    const varConfig = VARIABLES_CONFIG.find(v => v.folder === selectedFolder)
    const estacion  = estaciones.find(e => e.id === selectedEstId)

    // ── filtering ────────────────────────────────────────────────────────────

    const filterRows = (folder, estId, from, to) => {
        const result = []

        for (const year of getYearsInRange(from, to)) {
            const data = cache.current[`${folder}_${year}`]
            if (!data) continue

            const iEstac = data.estaciones.findIndex(e => e.id === estId)
            if (iEstac < 0) continue

            const obs    = data.observaciones[0]
            const valores = obs.datos[iEstac]

            for (let j = 0; j < valores.length; j++) {
                const fechaStr = data.fechas[obs.iFechas[j]]
                const dateStr  = fechaStr.slice(0, 10)   // 'YYYY-MM-DD'
                if (dateStr >= from && dateStr <= to) {
                    result.push({ fechaStr, valor: valores[j] })
                }
            }
        }

        result.sort((a, b) => a.fechaStr.localeCompare(b.fechaStr))
        return result
    }

    // ── data loading ─────────────────────────────────────────────────────────

    const loadData = async (folder, estId, from, to) => {
        setLoading(true)
        setError(null)

        const years      = getYearsInRange(from, to)
        const baseUrl    = globalConfig.URL_DATOS_ABIERTOS

        try {
            // Fetch missing year files in parallel; missing years are skipped silently
            await Promise.all(years.map(async (year) => {
                const key = `${folder}_${year}`
                if (cache.current[key]) return
                try {
                    const resp = await fetch(`${baseUrl}${folder}/${year}.mch`)
                    if (resp.ok) cache.current[key] = await resp.json()
                } catch (_) {
                    // network error for this year → skip
                }
            }))

            // Derive station list from the first available file for this folder
            const firstData = years.map(y => cache.current[`${folder}_${y}`]).find(Boolean)
            const newEstaciones = firstData?.estaciones ?? []
            setEstaciones(newEstaciones)

            // Resolve effective station id
            let effectiveEstId = estId
            if (!effectiveEstId || !newEstaciones.find(e => e.id === effectiveEstId)) {
                effectiveEstId = newEstaciones[0]?.id ?? null
            }
            setSelectedEstId(effectiveEstId)

            if (!effectiveEstId) {
                setRows([])
                return
            }

            setRows(filterRows(folder, effectiveEstId, from, to))
            setPage(0)

        } catch (_) {
            setError('Error al cargar los datos. Intente nuevamente.')
        } finally {
            setLoading(false)
        }
    }

    // Initial load on mount
    useEffect(() => {
        loadData(selectedFolder, null, dateFrom, dateTo)
    }, []) // eslint-disable-line

    // ── handlers ─────────────────────────────────────────────────────────────

    const handleVariableChange = (folder) => {
        setSelectedFolder(folder)
        setEstaciones([])
        setSelectedEstId(null)
        setRows([])
        loadData(folder, null, dateFrom, dateTo)
    }

    const handleEstacionChange = (estId) => {
        setSelectedEstId(estId)
        setRows(filterRows(selectedFolder, estId, dateFrom, dateTo))
        setPage(0)
    }

    const handleBuscar = () => {
        loadData(selectedFolder, selectedEstId, dateFrom, dateTo)
    }

    // ── CSV export ───────────────────────────────────────────────────────────

    const exportCSV = () => {
        const lines = [
            `Fecha,${varConfig?.nombre} (${varConfig?.unidad})`,
            ...rows.map(r => `"${formatFecha(r.fechaStr)}",${r.valor ?? ''}`)
        ]
        const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
        const url  = URL.createObjectURL(blob)
        const a    = document.createElement('a')
        a.href     = url
        a.download = `${varConfig?.nombre}_${estacion?.nombre ?? ''}_${dateFrom}_${dateTo}.csv`
            .replace(/\s+/g, '_')
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }

    // ── render ───────────────────────────────────────────────────────────────

    const paginatedRows = rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage)

    return (
        <Container maxWidth={false} sx={{ py: 2, px: 2, bgcolor: '#fff', minHeight: '100vh' }}>


            {/* Selectors */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                        flexWrap="wrap"
                        useFlexGap
                        alignItems={{ md: 'center' }}
                    >
                        <TextField
                            select
                            label="Variable"
                            value={selectedFolder}
                            onChange={e => handleVariableChange(e.target.value)}
                            size="small"
                            sx={{ minWidth: 240 }}
                        >
                            {VARIABLES_CONFIG.map(v => (
                                <MenuItem key={v.folder} value={v.folder}>
                                    {v.nombre}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            label="Estación"
                            value={selectedEstId ?? ''}
                            onChange={e => handleEstacionChange(e.target.value)}
                            size="small"
                            sx={{ minWidth: 190 }}
                            disabled={estaciones.length === 0}
                        >
                            {estaciones.map(e => (
                                <MenuItem key={e.id} value={e.id}>
                                    {e.displayNamePublic || e.nombre}
                                </MenuItem>
                            ))}
                        </TextField>

                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setModalEstaciones(true)}
                            disabled={estaciones.length === 0}
                            sx={{ alignSelf: 'center', whiteSpace: 'nowrap' }}
                        >
                            Ver estaciones
                        </Button>

                        <TextField
                            label="Desde"
                            type="date"
                            value={dateFrom}
                            onChange={e => setDateFrom(e.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: `${MIN_YEAR}-01-01`, max: dateTo }}
                        />

                        <TextField
                            label="Hasta"
                            type="date"
                            value={dateTo}
                            onChange={e => setDateTo(e.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: dateFrom, max: todayStr() }}
                        />

                        <Button
                            variant="contained"
                            onClick={handleBuscar}
                            disabled={loading}
                        >
                            Buscar
                        </Button>
                    </Stack>
                </CardContent>
            </Card>

            {/* Loading */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Error */}
            {error && !loading && (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            )}

            {/* Aviso y licencia */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Aviso:</strong> Los datos publicados son registros crudos sin validación, obtenidos
                    directamente por sensores de estaciones automáticas de la red de estaciones de INUMET.
                    Pueden contener errores instrumentales o vacíos de información. Las fechas y horas se
                    presentan en huso horario local (Uruguay, UTC-3).
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Datos licenciados bajo la{' '}
                    <a
                        href="https://www.gub.uy/agencia-gobierno-electronico-sociedad-informacion-conocimiento/sites/agencia-gobierno-electronico-sociedad-informacion-conocimiento/files/documentos/publicaciones/licencia_de_datos_abiertos_0.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'inherit' }}
                    >
                        Licencia de Datos Abiertos del Uruguay (DAG)
                    </a>.
                </Typography>
            </Box>

            {/* Results */}
            {!loading && rows.length > 0 && (
                <Card>
                    <CardContent>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            flexWrap="wrap"
                            sx={{ mb: 2 }}
                            gap={1}
                        >
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <Typography variant="body2" color="text.secondary">
                                    {rows.length.toLocaleString('es-UY')} registros
                                </Typography>
                                {estacion && (
                                    <Chip
                                        label={estacion.displayNamePublic || estacion.nombre}
                                        size="small"
                                        color="primary"
                                    />
                                )}
                                {varConfig && (
                                    <Chip
                                        label={`${varConfig.nombre} (${varConfig.unidad})`}
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                            </Stack>

                            <Button variant="outlined" size="small" onClick={exportCSV}>
                                Exportar CSV
                            </Button>
                        </Stack>

                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            {varConfig?.nombre} ({varConfig?.unidad})
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedRows.map((row, i) => (
                                        <TableRow key={i} hover>
                                            <TableCell>{formatFecha(row.fechaStr)}</TableCell>
                                            <TableCell align="right">
                                                {row.valor != null
                                                    ? row.valor
                                                    : <Typography component="span" variant="body2" color="text.disabled">—</Typography>
                                                }
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            component="div"
                            count={rows.length}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0) }}
                            rowsPerPageOptions={[25, 50, 100]}
                            labelRowsPerPage="Filas por página:"
                            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                        />
                    </CardContent>
                </Card>
            )}

            {/* Empty state */}
            {!loading && !error && rows.length === 0 && estaciones.length > 0 && (
                <Alert severity="info">
                    No hay datos para el período y estación seleccionados.
                </Alert>
            )}

            {/* Modal estaciones */}
            <EstacionesModal
                open={modalEstaciones}
                onClose={() => setModalEstaciones(false)}
                estaciones={estaciones}
            />

        </Container>
    )
}

export default DatosAbiertos
